import React, { useState, useRef, useEffect, useCallback, useMemo, useLayoutEffect } from 'react';
import './CourseAlignmentOverview.css';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useNavigate, useLocation } from 'react-router-dom';
import { exportExcelReport } from '../services/api';
import { useSelectedTemplate } from '../context/SelectedTemplateContext';
import { useNotification, NotificationContainer } from './Notification';

// Register ChartJS components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// Set Chart.js defaults to optimize performance
ChartJS.defaults.font.family = "'Instrument Sans', sans-serif";
ChartJS.defaults.responsive = true;
ChartJS.defaults.maintainAspectRatio = false;

const CourseAlignmentOverview = ({ coursesData: propCoursesData, visibleLearningGoals, courseToLGMap, alignmentData, indirectScores, isLoading, surveys, selectedSurvey }) => {
  const location = useLocation();
  const [year, setYear] = useState('2025 Spring');
  const [showYearDropdown, setShowYearDropdown] = useState(false);
  const [showRubrics, setShowRubrics] = useState(true);
  const [selectedKeyword, setSelectedKeyword] = useState(null);
  const [showEvidenceModal, setShowEvidenceModal] = useState(false);
  const [selectedEvidence, setSelectedEvidence] = useState(null);
  const chartRef = useRef(null);
  const [tableData, setTableData] = useState([]);
  const navigate = useNavigate();
  const chartInstanceRef = useRef(null);
  const [chartInstance, setChartInstance] = useState(null);
  const isMountedRef = useRef(true);
  
  const { selectedTemplate } = useSelectedTemplate();
  const [exportLoading, setExportLoading] = useState(false);
  
  // Add notification system
  const { notifications, addNotification, removeNotification } = useNotification();
  
  // Track if we're returning from evidence page
  const isReturningFromEvidence = useMemo(() => {
    return location.state?.returnedFromEvidence || 
           sessionStorage.getItem('return_from_evidence_state') !== null;
  }, [location.state]);
  
  // Check for restored table data from navigation
  useEffect(() => {
    // Only use pre-computed table data if we're returning from evidence page
    if (location.state?.originalTableData && tableData.length === 0 && isReturningFromEvidence) {
      //console.log("Using pre-computed table data from navigation state");
      setTableData(location.state.originalTableData);
    }
  }, [location.state, tableData.length, isReturningFromEvidence]);
  
  // Sample data for the courses and their alignment scores
  const coursesData = propCoursesData || [];
  
  // Check if we're in single course mode
  const isSingleCourseMode = coursesData.length === 1;
  
  // Calculate if we need scrolling - always true if more than 6 bars
  const needsScrolling = coursesData.length > 6;
  
  // Set the chart width - for fewer than 6 courses, use container width
  const chartWidth = needsScrolling ? `${Math.max(800, coursesData.length * 120)}px` : '100%';
  
  // Set chart height - taller for single course mode
  const chartHeight = isSingleCourseMode ? '300px' : '220px';
  
  // Function to get color based on score - matching the 4-level rubric colors
  const getScoreColor = useCallback((score) => {
    if (score >= 75) return '#468171'; // Emphasized - green
    if (score >= 50) return '#E8A84B'; // Reinforced - yellow-orange  
    if (score >= 25) return '#AF5C4A'; // Introduced - red
    return '#9ca3af'; // Covered Incidentally - gray
  }, []);

  // Function to normalize score to 4-level rubric system
  const normalizeScoreToRubricLevel = useCallback((score) => {
    if (score >= 75) return 4; // Emphasized - Level 4
    if (score >= 50) return 3; // Reinforced - Level 3
    if (score >= 25) return 2; // Introduced - Level 2
    return 1; // Covered Incidentally - Level 1
  }, []);
  
  // Chart Instance reference callback
  const onChartRef = useCallback(ref => {
    chartInstanceRef.current = ref;
    if (ref) {
      setChartInstance(ref);
    }
  }, []);
  
  // Generate table data once on mount or when necessary inputs change
  useEffect(() => {
    // Skip if we already have table data and we're returning from evidence
    if (tableData.length > 0 && isReturningFromEvidence) {
      //console.log("Skipping table data generation, using existing data");
      return;
    }
    
    // Add error handling to prevent infinite renders
    try {
      console.log("Generating CourseAlignmentOverview table data", 
        { hasAlignmentData: !!alignmentData, coursesCount: coursesData?.length });
      
      // Create full table data for all courses
      const newTableData = [];
    
    // If we have alignment data, process it directly
    if (alignmentData) {
      // For single course mode, we may have a different format of alignmentData
      if (isSingleCourseMode && coursesData.length > 0 && !alignmentData[coursesData[0].name]) {
        // Check if alignmentData is the course data itself rather than a map of courses
        // This can happen when returning from EvidenceDetailPage
        const courseName = coursesData[0].name;
        const courseData = Object.values(alignmentData)[0];
        
        if (courseData && courseData.evidence) {
          //console.log(`Processing single course data for ${courseName}`);
          
          // Calculate direct method score as average of all LG scores in evidence
          let directMethodScore = 0;
          let lgCount = 0;
          
          const lgKeys = Object.keys(courseData.evidence);
          lgKeys.forEach(lgKey => {
            const lgData = courseData.evidence[lgKey];
            if (lgData && typeof lgData.overall_score === 'number') {
              directMethodScore += lgData.overall_score;
              lgCount++;
            }
          });
          
          // Calculate average if we have any scores
          directMethodScore = lgCount > 0 ? directMethodScore / lgCount : 0;
          
          // Get indirect method score from course data first, then from props, or default to N/A
          let indirectMethodScore = 'N/A';
          if (courseData.indirect_method_score !== undefined) {
            indirectMethodScore = courseData.indirect_method_score;
          } else if (indirectScores && indirectScores[courseName]) {
            indirectMethodScore = indirectScores[courseName];
          }
          
          // Calculate alignment score as average of direct and indirect method scores
          let alignmentScore;
          if (indirectMethodScore !== 'N/A') {
            alignmentScore = (directMethodScore + parseFloat(indirectMethodScore)) / 2;
          } else {
            alignmentScore = directMethodScore;
          }
          
          // Format evidence text
          let evidence = 'No evidence found';
          let materialCount = 0;
          
          // Count materials across all learning goals
          const allMaterials = new Set();
          lgKeys.forEach(lgKey => {
            const lgEvidence = courseData.evidence[lgKey];
            if (lgEvidence.materials) {
              Object.keys(lgEvidence.materials).forEach(materialId => {
                allMaterials.add(materialId);
              });
            }
          });
          
          materialCount = allMaterials.size;
          if (materialCount > 0) {
            evidence = `${materialCount} materials`;
          }
          
          // Create the row data
          newTableData.push({
            courseName: courseName,
            evidence: evidence,
            directMethodScore: directMethodScore.toFixed(1),
            indirectMethodScore: indirectMethodScore,
            alignmentScore: alignmentScore.toFixed(1),
            colorClass: alignmentScore >= 75 ? 'high-score' : (alignmentScore >= 50 ? 'medium-score' : (alignmentScore >= 25 ? 'low-score' : 'incidental-score')),
            originalData: courseData
          });
        }
      } else {
        // Standard processing for multiple courses
      // For each course in the alignment data (e.g., BADM311)
      Object.keys(alignmentData).filter(key => key !== 'indirect_score').forEach(courseName => {
        const courseData = alignmentData[courseName];
        
        // Get the alignment score - specifically use the combined LG score (e.g., LG1)
        // Find the key that doesn't have a decimal point (e.g., LG1 instead of LG1.1)
        const lgScoreKeys = Object.keys(courseData.alignment_score);
        const mainLGKey = lgScoreKeys.find(key => !key.includes('.')) || lgScoreKeys[0];
        // We'll use directMethodScore as the alignment score since there's no indirect method score
        
        // Calculate direct method score as average of all LG scores in evidence
        let directMethodScore = 0;
        let lgCount = 0;
        
        const lgKeys = Object.keys(courseData.evidence);
        lgKeys.forEach(lgKey => {
          const lgData = courseData.evidence[lgKey];
          if (lgData && typeof lgData.overall_score === 'number') {
            directMethodScore += lgData.overall_score;
            lgCount++;
          }
        });
        
        // Calculate average if we have any scores
        directMethodScore = lgCount > 0 ? directMethodScore / lgCount : 0;
        
        // Get indirect method score from course data first, then from props, or default to N/A
        let indirectMethodScore = 'N/A';
        if (courseData.indirect_method_score !== undefined) {
          indirectMethodScore = courseData.indirect_method_score;
        } else if (indirectScores && indirectScores[courseName]) {
          indirectMethodScore = indirectScores[courseName];
        }
        
        // Calculate alignment score as average of direct and indirect method scores
        let alignmentScore;
        if (indirectMethodScore !== 'N/A') {
          alignmentScore = (directMethodScore + parseFloat(indirectMethodScore)) / 2;
        } else {
          alignmentScore = directMethodScore;
        }
        
        // Determine color class based on 4-level rubric score
        const getColorClass = (score) => {
          if (score >= 75) return 'high-score'; // Emphasized
          if (score >= 50) return 'medium-score'; // Reinforced
          if (score >= 25) return 'low-score'; // Introduced
          return 'incidental-score'; // Covered Incidentally
        };
        
        // Extract evidence data - show first material title and count
        let evidence = '0';
        let materialCount = 0;
        let firstMaterialTitle = '';
        
        // Get material count across all learning goals
        const evidenceLgKeys = Object.keys(courseData.evidence);
        const allMaterials = new Set(); // Use a Set to avoid counting duplicates
        
        // Loop through all learning goals and count unique materials
        evidenceLgKeys.forEach(lgKey => {
          const lgEvidence = courseData.evidence[lgKey];
          if (lgEvidence.materials) {
            Object.keys(lgEvidence.materials).forEach(materialId => {
              allMaterials.add(materialId);
            });
          }
        });
        
        materialCount = allMaterials.size;
        
        // Get first material's title from first LG (for display purposes)
          let firstLGWithMaterials = null;
          
          // Find the first LG that has materials
          for (const lgKey of evidenceLgKeys) {
            const lgEvidence = courseData.evidence[lgKey];
            if (lgEvidence.materials && Object.keys(lgEvidence.materials).length > 0) {
              firstLGWithMaterials = lgKey;
            const materialIds = Object.keys(lgEvidence.materials);
              const firstMaterial = lgEvidence.materials[materialIds[0]];

              if (firstMaterial.info_dict && firstMaterial.info_dict[0] && firstMaterial.info_dict[0].Title) {
                firstMaterialTitle = firstMaterial.info_dict[0].Title;
              }
                break;
            }
          }
          
          // Format evidence text if we found a material
          if (firstMaterialTitle) {
            evidence = `${materialCount}`;
          }
        
        // Create the row data
        newTableData.push({
          courseName: courseName, // Use the outermost key as course name
          evidence: evidence,
          directMethodScore: directMethodScore.toFixed(1),
          indirectMethodScore: indirectMethodScore,
          alignmentScore: alignmentScore.toFixed(1),
          colorClass: getColorClass(alignmentScore),
          originalData: courseData
        });
      });
      }
    } 
    // If no alignment data, use the existing course data
    else if (coursesData && coursesData.length > 0) {
      coursesData.forEach(course => {
        // Make sure score exists and is a number, or default to 0
        const score = typeof course.score === 'number' ? course.score : 0;
        
        // Create default data
        newTableData.push({
          courseName: course.name,
          evidence: 'Pending alignment analysis',
          directMethodScore: score.toFixed(1), 
          indirectMethodScore: 'N/A', // Set to N/A
          alignmentScore: score.toFixed(1), // Same as direct method score
          colorClass: score >= 75 ? 'high-score' : (score >= 50 ? 'medium-score' : (score >= 25 ? 'low-score' : 'incidental-score')),
          originalData: null
        });
      });
    }
    
    // Only set state if component is still mounted
    if (isMountedRef.current) {
      setTableData(newTableData);
    }
    } catch (error) {
      console.error('Error generating table data:', error);
      // Set empty table data to prevent infinite renders, only if mounted
      if (isMountedRef.current) {
        setTableData([]);
      }
    }
  }, [alignmentData, coursesData, isSingleCourseMode, isReturningFromEvidence, selectedTemplate?.id, indirectScores]);
  
  // Handle clicking on evidence link - use useCallback
  const handleEvidenceClick = useCallback((row) => {
    if (row.originalData) {
      //console.log("Navigating to evidence page with data:", row);
      
      // Get the school parameter from the URL
      const urlParams = new URLSearchParams(window.location.search);
      const school = urlParams.get('school') || 'MARYLAND';
      
      // Capture full alignment data and current page state
      const fullState = {
          courseData: row.originalData,
        courseName: row.courseName,
        fullAlignmentData: alignmentData,
        originalTableData: tableData,
        isSingleCourseMode: isSingleCourseMode,
        allCoursesData: coursesData,
        indirectScores: indirectScores,
        surveys: surveys,
        selectedSurvey: selectedSurvey
      };
      
      // Store state in sessionStorage to ensure it's available when returning
      try {
        sessionStorage.setItem('course_alignment_state', JSON.stringify(fullState));
        //console.log("Saved course alignment state to sessionStorage");
      } catch (err) {
        console.error("Failed to save state to sessionStorage:", err);
    }
      
      // Navigate to the evidence detail page with the course data and school parameter
      navigate(`/evidence-detail?school=${school}`, {
        state: fullState
      });
    }
  }, [navigate, alignmentData, tableData, isSingleCourseMode, coursesData, indirectScores, surveys, selectedSurvey]);
  
  // Memoize years array to prevent recreation
  const years = useMemo(() => [
    '2025 Spring',
    '2024 Fall',
    '2024 Spring',
    '2023 Fall'
  ], []);
  
  const handleYearClick = useCallback(() => {
    setShowYearDropdown(!showYearDropdown);
  }, [showYearDropdown]);
  
  const selectYear = useCallback((selectedYear) => {
    setYear(selectedYear);
    setShowYearDropdown(false);
  }, []);
  
  const toggleRubrics = useCallback(() => {
    setShowRubrics(!showRubrics);
  }, [showRubrics]);
  
  // Format learning goal ID properly (e.g., convert "11" to "1.1", "12" to "1.2")
  const formatLearningGoalId = (id) => {
    // If it's already in the correct format (contains a dot), return as is
    if (String(id).includes('.')) {
      return id;
    }
    
    // If it's a string with 2 or more digits, insert a dot after the first digit
    const idStr = String(id);
    if (idStr.length >= 2) {
      return `${idStr[0]}.${idStr.substring(1)}`;
    }
    
    // Otherwise, return as is
    return id;
  };

  const handleExportSection = useCallback(async () => {
    try {
      if (!alignmentData || Object.keys(alignmentData).length === 0) {
        addNotification('No alignment data available to export.', 'warning');
        return;
      }
      
      setExportLoading(true);
      
      // Build details object for API request
      const details = {};
      
      // For Excel export, we include all courses with their learning goals
      Object.keys(alignmentData).filter(key => key !== 'indirect_score').forEach(courseName => {
        // For now, we're including all identified learning goals for each course
        // This approach follows the requirement to disable section selection
        
        // Get all available learning goal IDs for this course
        const courseData = alignmentData[courseName];
        const allLearningGoals = new Set();
        
        // Collect all learning goals from evidence
        if (courseData.evidence) {
          Object.keys(courseData.evidence).forEach(lgKey => {
            // Only include decimal learning goals (e.g., 2.1, not 2)
            if (lgKey.includes('.')) {
              allLearningGoals.add(parseFloat(lgKey));
            }
          });
        }
        
        // If no specific learning goals found, try to get them from alignment score
        if (allLearningGoals.size === 0 && courseData.alignment_score) {
          Object.keys(courseData.alignment_score).forEach(lgKey => {
            if (lgKey.includes('.')) {
              allLearningGoals.add(parseFloat(lgKey));
            }
          });
        }
        
        // Convert set to array for API
        const learningGoals = Array.from(allLearningGoals).map(lg => {
          // Format learning goal IDs properly (e.g., convert 11 to 1.1)
          return formatLearningGoalId(lg);
        });
        
        // Only include course if it has learning goals
        if (learningGoals.length > 0) {
          details[courseName] = learningGoals;
        }
      });
      
      // Make API call if there are courses with learning goals
      if (Object.keys(details).length > 0) {
        // Use the selected template ID or default to 1
        const templateId = selectedTemplate?.id || 1;
        const response = await exportExcelReport(templateId, details);
        
        // Handle the new response format which returns a single download link
        if (response && response.download_link) {
          // Ensure download_link is a string before using it
          const downloadUrl = typeof response.download_link === 'string' 
            ? response.download_link 
            : String(response.download_link);
          
          // Open the download link in a new tab
          window.open(downloadUrl, '_blank');
        } else {
          throw new Error('Invalid response format from export API');
        }
      } else {
        addNotification('No course data available to export.', 'warning');
      }
    } catch (error) {
      console.error('Error exporting section:', error);
      addNotification('Failed to export report. Please try again later.', 'error');
    } finally {
      setExportLoading(false);
    }
  }, [alignmentData, selectedTemplate, addNotification]);

  // Chart.js configuration - memoize to prevent recreation
  const chartData = useMemo(() => ({
    labels: tableData.map(course => course.courseName),
    datasets: [
      {
        data: tableData.map(course => {
          // Convert to number safely or return 0 if not a valid number
          const score = course.alignmentScore === 'N/A' ? 0 : parseFloat(course.alignmentScore) || 0;
          // Normalize to 4-level rubric system (1-4)
          return normalizeScoreToRubricLevel(score);
        }),
        backgroundColor: tableData.map(course => {
          // Convert to number safely or return 0 if not a valid number
          const score = course.alignmentScore === 'N/A' ? 0 : parseFloat(course.alignmentScore) || 0;
          return getScoreColor(score);
        }),
        borderWidth: 0,
        borderRadius: 3,
        barPercentage: isSingleCourseMode ? 0.3 : (coursesData.length <= 6 ? 0.5 : 0.8),
        categoryPercentage: isSingleCourseMode ? 0.5 : (coursesData.length <= 6 ? 0.8 : 0.9),
      }
    ]
  }), [tableData, getScoreColor, normalizeScoreToRubricLevel, coursesData.length, isSingleCourseMode]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: isReturningFromEvidence ? 0 : 1000, // Disable animation when returning from evidence
    },
    layout: {
      padding: {
        left: 10,
        right: 10
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        min: 0,
        max: 4,
        grid: {
          color: '#ddd',
          borderDash: [5, 5],
          drawBorder: false,
        },
        ticks: {
          font: {
            size: 12,
          },
          color: '#777',
          stepSize: 1,
          callback: function(value) {
            const labels = {
              0: '',
              1: 'Incidental',
              2: 'Introduced', 
              3: 'Reinforced',
              4: 'Emphasized'
            };
            return labels[value] || '';
          }
        },
        title: {
          display: true,
          text: 'Coverage Level',
          font: {
            size: 14,
            weight: 500,
          },
          color: '#333',
          padding: { bottom: 10 }
        }
      },
      x: {
        grid: {
          display: false,
          drawBorder: false
        },
        ticks: {
          font: {
            size: isSingleCourseMode ? 14 : 10,
            weight: isSingleCourseMode ? 'bold' : 'normal',
          },
          color: '#333',
          maxRotation: 0,
          autoSkip: false
        },
        title: {
          display: !isSingleCourseMode, // Hide title in single course mode
          text: isSingleCourseMode ? '' : 'Courses',
          font: {
            size: 14,
            weight: 500,
          },
          color: '#333',
          padding: { top: 10 }
        },
        // Limit visible range to 6 bars
        min: 0,
        // For fewer courses, distribute bars evenly
        distribution: coursesData.length <= 6 ? 'linear' : 'series'
      }
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: !isReturningFromEvidence, // Disable tooltips when returning from evidence
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        titleColor: '#333',
        bodyColor: '#333',
        borderColor: '#ddd',
        borderWidth: 1,
        cornerRadius: 4,
        caretSize: 6,
        boxPadding: 6,
        displayColors: false,
        callbacks: {
          title: (tooltipItems) => {
            return tooltipItems[0].label;
          },
          label: (context) => {
            const levelLabels = {
              1: 'Covered Incidentally',
              2: 'Introduced',
              3: 'Reinforced', 
              4: 'Emphasized'
            };
            return `Level: ${levelLabels[context.parsed.y] || 'Unknown'}`;
          }
        }
      }
    }
  }), [coursesData.length, isSingleCourseMode, isReturningFromEvidence]);
  
  // Use useLayoutEffect to ensure scrollbar setup happens before paint
  useLayoutEffect(() => {
    if (chartRef.current) {
      const chartCanvas = chartRef.current.canvas;
      const canvas = chartRef.current.canvas;
      const wrapper = document.querySelector('.chart-container-wrapper');
      
      if (needsScrolling) {
        // For more than 6 courses, set a minimum width that will ensure scrollbar is visible
        canvas.parentNode.style.minWidth = `${Math.max(800, coursesData.length * 120)}px`;
        
        // Force scroll to show 1px to ensure scrollbar appears
        setTimeout(() => {
          if (wrapper) {
            // Show scrollbar
            wrapper.style.overflowX = 'auto';
            // Create some space to scroll even with 6 bars
            wrapper.scrollLeft = 1;
            // Add a dummy div to force scrollbar to appear
            if (!document.querySelector('.scrollbar-spacer')) {
              const spacer = document.createElement('div');
              spacer.className = 'scrollbar-spacer';
              spacer.style.width = '1px';
              spacer.style.height = '1px';
              spacer.style.position = 'absolute';
              spacer.style.right = '-50px';
              canvas.parentNode.appendChild(spacer);
            }
          }
        }, 10); // Reduced timeout for faster display
      } else {
        // For 6 or fewer courses, ensure the chart expands to fill the available space
        canvas.parentNode.style.width = '100%';
        canvas.parentNode.style.maxWidth = '100%';
        canvas.parentNode.style.minWidth = '100%';
        
        // Hide scrollbar completely
        if (wrapper) {
          wrapper.style.overflowX = 'hidden';
        }
        
        // Remove any spacers if they exist
        const spacer = document.querySelector('.scrollbar-spacer');
        if (spacer) {
          spacer.remove();
        }
      }
    }
  }, [chartRef, coursesData.length, needsScrolling]);
  
  // Clean up on unmount
  useEffect(() => {
    return () => {
      // Mark component as unmounted
      isMountedRef.current = false;
      // Clean up chart instance on unmount
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }
    };
  }, []);
  
  // Evidence Modal - memoize the component
  const EvidenceModal = useCallback(({ evidence, onClose }) => {
    if (!evidence) return null;
    
    // Get the first learning goal's evidence
    const firstLGKey = Object.keys(evidence.evidence)[0];
    const lgEvidence = evidence.evidence[firstLGKey];
    
    // Count materials
    const materialCount = lgEvidence.materials ? Object.keys(lgEvidence.materials).length : 0;
    
    return (
      <div className="evidence-modal-overlay">
        <div className="evidence-modal">
          <div className="evidence-modal-header">
            <h3>Evidence Details ({materialCount} materials)</h3>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
          
          <div className="evidence-modal-content">
            <h4>Keywords</h4>
            <div className="evidence-keywords">
              {Object.entries(lgEvidence.keywords)
                .filter(([_, value]) => value > 0)
                .map(([keyword, score]) => (
                  <div 
                    key={keyword} 
                    className="evidence-keyword-item"
                    onClick={() => setSelectedKeyword(keyword)}
                  >
                    <div className="keyword-name">{keyword}</div>
                    <div className="keyword-score">{score.toFixed(1)}%</div>
                  </div>
                ))
              }
            </div>
            
            {selectedKeyword && lgEvidence.materials && (
              <div className="keyword-materials">
                <h4>Materials for "{selectedKeyword}"</h4>
                {Object.entries(lgEvidence.materials).map(([materialName, material]) => (
                  <div key={materialName} className="material-item">
                    <h5>{material.info_dict?.Title || materialName}</h5>
                    
                    {material.segments && Object.entries(material.segments).map(([segment, segmentData]) => (
                      <div key={segment} className="segment-item">
                        <div className="segment-header">
                          <span>{segment}</span>
                          {segmentData.material_link_with_timestamp && (
                            <a 
                              href={segmentData.material_link_with_timestamp} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="segment-link"
                            >
                              View in Canvas
                            </a>
                          )}
                        </div>
                        
                        {segmentData.screenshot_dict?.keyword_dict?.[selectedKeyword] && (
                          <div className="segment-screenshot">
                            <img 
                              src={segmentData.screenshot_dict.keyword_dict[selectedKeyword]} 
                              alt={`Screenshot of ${selectedKeyword}`} 
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="evidence-modal-footer">
            <button onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }, [selectedKeyword]);
  
  // Log component re-renders with timing
  useEffect(() => {
    const renderTime = performance.now();
    console.log(`CourseAlignmentOverview rendered in ${renderTime.toFixed(2)}ms`, { 
      isSingleCourseMode, 
      coursesCount: coursesData.length,
      isReturningFromEvidence
    });
  });

  return (
    <div className="course-alignment-overview">
      {isLoading ? (
        <div className="alignment-loading">
          <p>Loading alignment data...</p>
        </div>
      ) : (
        <>
          {exportLoading && (
            <div className="export-overlay">
              <div className="export-loading-container">
                <div className="loading-indicator"></div>
                <p>Exporting Excel report...</p>
              </div>
            </div>
          )}
          
          <div className="alignment-header">
            <div className="header-controls">
              <div className="academic-year-selector-1">
                <label>Academic Year:</label>
                <div className="selector-dropdown-container">
                  <div className="selector-dropdown" onClick={handleYearClick}>
                    <span>{year}</span>
                    <span className="dropdown-arrow">▼</span>
                  </div>
                  {showYearDropdown && (
                    <div className="year-dropdown">
                      {years.map(yearOption => (
                        <div 
                          key={yearOption} 
                          className="year-option" 
                          onClick={() => selectYear(yearOption)}
                        >
                          {yearOption}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              
              
              <div className="rubrics-toggle">
                <label className="switch">
                  <input 
                    type="checkbox" 
                    checked={showRubrics}
                    onChange={toggleRubrics}
                  />
                  <span className="slider round"></span>
                </label>
                <span className="toggle-label">Show Rubrics</span>
              </div>
            </div>
          </div>
          
          {showRubrics && (
            <div className="rubrics-panel">
              <h3>Coverage Level</h3>
              <div className="rubric-items">
                <div className="rubric-item">
                  <div className="rubric-color" style={{ backgroundColor: '#9ca3af' }}></div>
                  <div className="rubric-label">&lt; 25: Covered Incidentally</div>
                </div>
                <div className="rubric-item">
                  <div className="rubric-color low"></div>
                  <div className="rubric-label">25-49: Introduced</div>
                </div>
                <div className="rubric-item">
                  <div className="rubric-color medium"></div>
                  <div className="rubric-label">50-74: Reinforced</div>
                </div>
                <div className="rubric-item">
                  <div className="rubric-color high"></div>
                  <div className="rubric-label">≥ 75: Emphasized</div>
                </div>
              </div>
            </div>
          )}
          
          <div className={`chart-container-wrapper ${!needsScrolling ? 'no-scroll' : ''}`}>
            <div className="chart-container" style={{ width: chartWidth, height: chartHeight }}>
              <Bar 
                ref={onChartRef}
                data={chartData} 
                options={chartOptions} 
                redraw={false} // Prevent complete redraw, just update
              />
            </div>
          </div>
          

          
          <div className="alignment-table-1">
            <table className="alignment-table-1">
              <thead>
                <tr>
                  <th className="center-align">Course Name</th>
                  <th className="center-align">Items of Evidence</th>
                  <th className="center-align">
                    Direct<br />Method Score
                  </th>
                  <th className="center-align">
                    Indirect<br />Method Score
                  </th>
                  <th className="center-align">
                    Alignment<br />Score
                  </th>
                </tr>
              </thead>
              <tbody>
                {tableData.map((row, index) => (
                  <tr key={index}>
                    <td className="center-align">{row.courseName}</td>
                    <td>
                      <a 
                        href="#" 
                        className="evidence-link"
                        onClick={(e) => {
                          e.preventDefault();
                          handleEvidenceClick(row);
                        }}
                      >
                        {row.evidence}
                      </a>
                    </td>
                    <td className="center-align">{row.directMethodScore}</td>
                    <td className="center-align">{row.indirectMethodScore}</td>
                    <td className={`center-align bold ${row.colorClass}`}>{row.alignmentScore}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {showEvidenceModal && (
            <EvidenceModal 
              evidence={selectedEvidence} 
              onClose={() => {
                setShowEvidenceModal(false);
                setSelectedKeyword(null);
              }}
            />
          )}
        </>
      )}
      
      <NotificationContainer 
        notifications={notifications} 
        removeNotification={removeNotification} 
      />
    </div>
  );
};

export default React.memo(CourseAlignmentOverview); 